<script lang="ts">
  /**
   * SESSION-CVD PANE — R502(1)(2)(3a) slice 1, on the LWC vehicle revived by R504.
   *
   * ONE INSTRUMENT, PEGGED TO PRICE. Price candles in pane 0 and the continuous/session CVD in
   * pane 1 share ONE time scale, because that shared axis is the whole point of the ruling: price
   * HH against CVD LH only reads by eye when both are on the same x. The custom canvas made this
   * hand-built; LWC gives it natively, which is the argument R504 revived the conversion on.
   *
   * ⛔ HEADS-UP, NOT A REPLACEMENT. This mounts ALONGSIDE the existing column. Nothing on the
   * owner's screen moves until he has judged this by eye — R502's own "pixels-first, ship slices".
   *
   * ⛔ EVERY NUMBER COMES FROM THE ARMED PURE LAYER, NONE IS COMPUTED HERE.
   * `sessionAnchor` -> `anchoredCumulative` -> `cvdCandles` / `sessionFlips`, all in `cvd2.ts`
   * with mutant-driven arms. A pane that re-derives a quantity is a second source of truth, and
   * the retired blue/orange strip is what that looks like once the two disagree.
   *
   * ⛔ THE CANVAS CANNOT READ `var(--token)`. Colours resolve through `lwcTokens`, and
   * `watchTheme` repaints on the app's own `spx-theme` dispatch — the same event `App.svelte`
   * fires. A pane that reads tokens once is correct until the first theme toggle and then lies.
   */
  import { onMount } from "svelte";
  import {
    createChart, createSeriesMarkers, CandlestickSeries, LineSeries, LineStyle,
    type IChartApi, type ISeriesApi, type UTCTimestamp, type Time, type IPriceLine,
    type ISeriesMarkersPluginApi,
  } from "lightweight-charts";
  import { resolveTokens, watchTheme, type ResolvedTokens } from "../../spike/lwcTokens";
  import type { ChartLevel } from "../chart/types/index.ts";
  import { API_BASE, subscribeServedVenueState, type VenueState } from "../api.ts";
  import type { Cvd2Feed, DrawableCvd, PriceBar } from "../cvd2";
  import { anchorNote, cvdGapMarkers, servedAnchorState, studyChip } from "../cvd2";
  import StalenessChip from "../StalenessChip.svelte";
  import { sessionSegments } from "../session";
  import { paneGaps } from "./paneGaps";
  import { barTimeMs, barTimeExtent, timeBinding } from "../rungFeed";
  import { computeMcStoch, mcStochLegend, MC_STOCH_WARMUP_BARS, type Candle } from "./mcStoch";
  import { leg } from "../feedLeg.ts";
  import { readVwapCoveragePage, vsplitWarmupWindow } from "../candlePaging.ts";
  import {
    buildCvdIntensity,
    detectCalibratedAbsorptions,
    detectCvdDivergences,
    detectCvdMarkers,
    detectExhaustionLows,
    divergenceWindow,
    formatContracts,
    rthSessionAt,
    type CvdDivergenceInput,
    type CvdDivergencePair,
  } from "./cvdIntensity.ts";
  import { type ResidueEntry, markerGapState } from "../order33State.ts";
  // ORDER 77 step 4 — the ghost's badge STATES come from the desk's PREREG_004 reading (R1297),
  // never from a rate written into this template. See prereg004Reading.ts for why the app carries
  // the desk's verdict rather than deriving one.
  import { PREREG_004_READING, ghostBadge } from "./prereg004Reading.ts";
  import {
    CVD_CANDLE_AUTOSCALE_MARGIN_FRACTION,
    CVD_CANDLE_AUTOSCALE_MIN_MARGIN,
    CVD_LINE_PANE,
    PANE_MIN_HEIGHTS,
    PRICE_PANE,
    VSPLIT_PANE,
    buildCvdCandleModel,
    buildGhostSegments,
    ghostAxisTitle,
    buildCvdStudyModel,
    cvdCandleAutoscaleRange,
    landingPaneHeights,
    type CvdDisplayMode,
    type GhostSegment,
    type GhostWallSnapshot,
  } from "./linkedStudyModel.ts";
  import type { RangeBar } from "./rangeState.ts";
  import {
    FOCUS_PROPORTIONS,
    defaultFocusLogicalRange,
    readFocusRange,
    writeFocusRange,
  } from "../focusLayout.ts";
  import { rungFromKey, rungLabel, type Rung, type RungKey } from "../rungLadder.ts";
  /** R1: the SAME derived-follow predicate the other ten importers use. Extend, never fork. */
  import { coversLiveEdge, landingLogicalRange, PRICE_PANE_MARGIN_FRAC } from "../chartViewport.ts";
  /** R2545: SessionCvdPane PASSES 0.08; the ten other importers keep chartViewport's 0.05. */
  const PANE_GUTTER_FRAC = 0.08;

  interface Props {
    /**
     * ⛔ NOT `Cvd2Bar[]`. A `DrawableCvd` can ONLY come from `admitCvd`, which mints it only when
     * `cvd2BlockedReason` returned null. ⇒ A CALLER CANNOT REACH THIS PANE WITH AN UNJUDGED FEED:
     * there is nothing of the right type to hand it. `null` means the feed was REFUSED, and the
     * refusal's sentence arrives as `cvdBlocked` — the two travel together by construction.
     * (GK-xx R526(2): a property held as a TYPE survives a change of vehicle; one held as
     * a-function-you-call does not — this pane lost exactly such a call once already.)
     */
    cvd: DrawableCvd | null;
    cvdFeed?: Cvd2Feed | null;
    cvdBarSpanMs?: number;
    /** Why the CVD leg was refused, when `cvd` is null. */
    cvdBlocked?: string | null;
    price: PriceBar[];
    /** OHLC for pane 0. Kept separate from `price` (closes) so neither is derived from the other. */
    declaredResidue?: readonly ResidueEntry[];
    candles?: { t: number; o: number; h: number; l: number; c: number; v: number | null }[];
    height?: number;
    /** Focus-only fitted outer height; null preserves the cockpit's three-pane stack. */
    focusHeight?: number | null;
    /** Changes only after the selected rung's new candle payload commits. */
    focusRungKey?: RungKey | null;
    /**
     * ⛔ WHO NAMES THIS CHART — R771④, resolved by 11 at the act (GK-am's COND, extended).
     * As a PAGE this pane named itself `ES · price + session CVD`. Under R771 it instantiates
     * TWICE on the cockpit at DIFFERENT timeframes, and a self-naming part then says the same
     * thing on both while naming neither rung.
     * ⇒ *** A SELF-NAMING COMPONENT IS FINE AS A PAGE AND WRONG AS A PART. ***
     * ⚠ AND THE HALF THAT IS LAW 2, NOT TASTE: this pane said `ES` while its host said `ES1!` —
     * two labels for ONE feed, six lines apart, on the owner's primary chart. The host fetched
     * the data and is the only one that knows the root AND the rung, so the host names it.
     * `null` keeps the standalone page's behaviour, so slice1 is untouched.
     */
    label?: string | null;
    /** The standalone page says "heads-up only"; embedded in the cockpit that sub-line is false. */
    standaloneNote?: boolean;
    /** Unique host-owned marker proving this native stack, not merely its surrounding card, mounts. */
    stackMarker?: string;
    /** Host-owned disclosure state. Embedded charts bind this to their single info toggle. */
    showInfo?: boolean;
    /** Host-owned id so the chart's one toggle can name both disclosure regions. */
    infoPanelId?: string;
    /**
     * DEALER LEVELS, ALREADY ES-MAPPED BY THE CALLER (R814). A raw SPX number on an ES axis is
     * ~15 points wrong and looks entirely plausible, so no conversion happens in here.
     */
    levels?: ChartLevel[];
    /** Off by default: a caller opts in exactly as it does for VWAP on the sibling renderer. */
    showLevels?: boolean;
    /** ORDER 20A screen role: markers live on A; confirmed divergence pairs live on B. */
    chartRole?: "lower" | "higher" | "standalone";
    /** Active rung key, used only for the measured divergence window/contract floor. */
    timeframe?: string;
    /** Higher-timeframe chart's visible window, shaded on the lower chart when it contains it. */
    activeWindow?: { from: number; to: number } | null;
    onVisibleWindowChange?: ((window: { from: number; to: number } | null) => void) | null;
    rangeFeed?: {
      ok: boolean;
      bars?: RangeBar[];
      age_s?: number | null;
      budget_s?: number | null;
      stale?: boolean | null;
      reason?: string;
    } | null;
    rangeTicks?: 8 | 12;
    onRangeTicksChange?: ((ticks: 8 | 12) => void) | null;
    /** A focus tier may pin the served accumulator to one display without changing user prefs. */
    displayMode?: CvdDisplayMode | null;
    onDisplayModeChange?: ((mode: CvdDisplayMode) => void) | null;
    /** Deliberate empty slot reserved for the future study verdict. */
    reserveVerdictSlot?: boolean;
    /** ORDER 22 wallAlert snapshots, strictly pre-event; absent means G5 conditioning is withheld. */
    ghostWalls?: GhostWallSnapshot[];
  }
  let {
    cvd, cvdFeed = null, cvdBarSpanMs = 60_000, cvdBlocked = null,
    price = [], candles = [], declaredResidue = [], height = 866, focusHeight = null, focusRungKey = null,
    label = null, standaloneNote = true,
    stackMarker = "ES LINKED STUDIES",
    showInfo = $bindable(false),
    infoPanelId = "session-cvd-info",
    /**
     * DEALER LEVELS ON THE OWNER'S CHART — R814, a PORT rather than a new idea. `LwcPriceCanvas`
     * has drawn these since 08-09 and the ES1! panes show them; the TradingView charts render
     * through THIS pane, which was never given the overlay.
     * ⛔ ALREADY ES-MAPPED BY THE CALLER — a raw SPX number on an ES axis is ~15 points wrong and
     * looks perfectly plausible. This pane does no conversion, so it cannot invent one.
     */
    levels = [] as ChartLevel[],
    showLevels = false,
    chartRole = "standalone",
    timeframe = "1",
    activeWindow = null,
    onVisibleWindowChange = null,
    rangeFeed = null,
    rangeTicks = 8,
    onRangeTicksChange = null,
    displayMode = null,
    onDisplayModeChange = null,
    reserveVerdictSlot = false,
    ghostWalls = [],
  }: Props = $props();

  let servedVenueState = $state<VenueState | null>(null);
  let servedVenueKnown = $state(false);
  let venueClosed = $derived(servedVenueState?.verdict === "CLOSED");

  type PaneCandle = { t: number; o: number; h: number; l: number; c: number; v: number | null };
  let vsplitWarmupCandles = $state<PaneCandle[]>([]);
  let vsplitWarmupReason = $state<string | null>(null);
  let vsplitWarmupAttemptKey = "";

  let studyCandles = $derived.by(() => {
    const first = candles[0]?.t;
    if (first === undefined) return [];
    return [...vsplitWarmupCandles.filter((bar) => bar.t < first), ...candles];
  });
  let mcStudy = $derived(computeMcStoch(studyCandles.map((c): Candle => ({
    open: c.o, high: c.h, low: c.l, close: c.c,
  }))));
  let warmup = $derived(mcStudy.warmup);
  let vsplitRung = $derived(rungFromKey(timeframe));
  let vsplitRungLabel = $derived(vsplitRung === null ? timeframe : rungLabel(vsplitRung));
  let vsplitSessionDate = $derived(candles.length === 0
    ? "unknown"
    : new Date(candles[0].t).toISOString().slice(0, 10));
  let vsplitLegend = $derived(mcStochLegend(warmup));

  async function loadVsplitWarmup(
    request: ReturnType<typeof vsplitWarmupWindow>,
    rung: Rung,
    attemptKey: string,
  ) {
    const response = await leg(`${API_BASE}${request.url}`, "vsplit-warmup");
    if (vsplitWarmupAttemptKey !== attemptKey) return;
    if (response.transport !== null) {
      vsplitWarmupReason = `WARM-UP REFUSED · ${response.transport}`;
      return;
    }
    const read = readVwapCoveragePage(response.body, rung, request.from, request.to, "ES");
    if (read.page === null) {
      vsplitWarmupReason = `WARM-UP REFUSED · ${read.blocked}`;
      console.warn(`[SessionCvdPane] ${vsplitWarmupReason}`);
      return;
    }
    const prefix = read.page.bars
      .filter((bar) => request.from <= bar.t && bar.t < request.to)
      .map((bar) => ({ ...bar }));
    vsplitWarmupCandles = prefix;
    vsplitWarmupReason = prefix.length < MC_STOCH_WARMUP_BARS.slow
      ? `WARM-UP SHORT ${prefix.length}/${MC_STOCH_WARMUP_BARS.slow}`
      : null;
    if (vsplitWarmupReason !== null) console.warn(`[SessionCvdPane] ${vsplitWarmupReason}`);
  }

  $effect(() => {
    const first = candles[0]?.t;
    const rung = rungFromKey(timeframe);
    if (first === undefined || candles.length >= MC_STOCH_WARMUP_BARS.slow) {
      vsplitWarmupAttemptKey = "";
      vsplitWarmupCandles = [];
      vsplitWarmupReason = null;
      return;
    }
    if (rung === null) {
      vsplitWarmupAttemptKey = "";
      vsplitWarmupCandles = [];
      vsplitWarmupReason = `WARM-UP REFUSED · unknown rung ${timeframe}`;
      return;
    }
    const attemptKey = `${timeframe}:${first}:${MC_STOCH_WARMUP_BARS.slow}`;
    if (vsplitWarmupAttemptKey === attemptKey) return;
    vsplitWarmupAttemptKey = attemptKey;
    vsplitWarmupCandles = [];
    vsplitWarmupReason = `WARM-UP READING · ${vsplitRungLabel}`;
    const request = vsplitWarmupWindow(rung, first, MC_STOCH_WARMUP_BARS.slow);
    void loadVsplitWarmup(request, rung, attemptKey);
  });

  /** The admitted bars, or none. Nothing downstream re-judges the feed. */
  let bars = $derived(cvd?.bars ?? []);

  /** Which legs have nothing to draw. Decided in `paneGaps` so an arm can reach the decision. */
  let gaps = $derived(paneGaps({ bars: bars.length, candles: candles.length }));

  const SESSION_LOOKBACK_MS = 4 * 24 * 60 * 60_000;
  const CVD_MODE_STORAGE_KEY = "spx-hub.session-cvd.mode";
  const CVD_MODE_EVENT = "spx-hub:cvd-mode";
  let cvdMode = $state<CvdDisplayMode>("continuous");

  function storedCvdMode(): CvdDisplayMode {
    try {
      return localStorage.getItem(CVD_MODE_STORAGE_KEY) === "session" ? "session" : "continuous";
    } catch (error) {
      console.warn(`[SessionCvdPane] CVD mode preference read failed — ${String(error)}`);
      return "continuous";
    }
  }

  function toggleCvdMode() {
    const next = cvdMode === "continuous" ? "session" : "continuous";
    if (displayMode !== null) {
      onDisplayModeChange?.(next);
      return;
    }
    cvdMode = next;
    try {
      localStorage.setItem(CVD_MODE_STORAGE_KEY, next);
    } catch (error) {
      console.warn(`[SessionCvdPane] CVD mode preference write failed — ${String(error)}`);
    }
    window.dispatchEvent(new CustomEvent<CvdDisplayMode>(CVD_MODE_EVENT, { detail: next }));
  }

  $effect(() => {
    if (displayMode !== null && cvdMode !== displayMode) cvdMode = displayMode;
  });

  let host = $state<HTMLDivElement | null>(null);
  let tokens = $state<ResolvedTokens | null>(null);
  let chart: IChartApi | null = null;
  let priceSeries: ISeriesApi<"Candlestick"> | null = null;
  let cvdCandleSeries: ISeriesApi<"Candlestick"> | null = null;
  const nativeCvdOwners = new WeakSet<ISeriesApi<"Candlestick">>();
  let cvdPivotMarkers: Array<ISeriesMarkersPluginApi<Time>> = [];
  let cvdGapMarkerLayer: ISeriesMarkersPluginApi<Time> | null = null;
  let divergencePriceMarkers: ISeriesMarkersPluginApi<Time> | null = null;
  let divergenceCvdMarkers: Array<ISeriesMarkersPluginApi<Time>> = [];
  let fastSeries: ISeriesApi<"Line"> | null = null;
  let slowSeries: ISeriesApi<"Line"> | null = null;
  let divergenceSeries: Array<{
    id: string;
    pair: CvdDivergencePair;
    series: ISeriesApi<"Line">;
  }> = [];
  let ghostSeries: Array<{ id: string; segment: GhostSegment; series: ISeriesApi<"Line"> }> = [];
  let zeroLine: IPriceLine | null = null;
  let windowShade = $state<{ left: number; width: number } | null>(null);
  let publishedWindow = "";
  let focusAppliedRung: RungKey | null = null;
  let focusPersistFrame: number | null = null;

  function persistManualFocusRange() {
    focusPersistFrame = null;
    if (focusHeight === null || focusRungKey === null || chart === null) return;
    const range = chart.timeScale().getVisibleLogicalRange();
    if (range !== null) writeFocusRange(focusRungKey, { from: range.from, to: range.to });
  }

  function scheduleFocusRangePersist() {
    if (focusHeight === null || focusRungKey === null) return;
    if (focusPersistFrame !== null) cancelAnimationFrame(focusPersistFrame);
    focusPersistFrame = requestAnimationFrame(persistManualFocusRange);
  }

  /**
   * R2 / R2545 / R2574: THE PANE'S LANDING WINDOW, from its OWN MEASURED PLOT at its own 0.08
   * gutter — the wiring that R2574 accepted is not one line, because this pane never called
   * followViewport and landed on focusLayout's fixed 180-and-6 instead.
   *
   * ⚠ TWO HONEST FALL-THROUGHS, neither of them an invented number:
   *   · a zero or unmeasurable plot is the PRE-LAYOUT state (R2 sets the window only after a
   *     first layout with a non-zero plot AND a first data response), so it keeps the old default
   *     rather than throwing into the render;
   *   · a SUBMIN rung has no declared bar width — R2's table runs 1m to 1d only — so it keeps the
   *     old default too. Inventing a width here is exactly what got R2's own 8 pct struck.
   * ⛔ AND THE KEY IS NOT THE TIMEFRAME: `timeframe` is a RungKey like "t:12", while the declared
   *    table is keyed "12". Passing the key straight through throws "no declared bar width".
   */
  /**
   * THE BAR COUNT THE LANDING WINDOW IS COMPUTED IN - the time scale's, not the candle array's.
   *
   * ⛔ THE CHART'S LOGICAL INDEX IS NOT THE CANDLE INDEX. The time scale carries every series' time
   * points, so on the served cockpit the logical range ran about 5 points past the fed candles
   * (HUDCHAT-38, candidate 60f05786, 18:3xZ 09-24: range to 2041 over 2,037 fed bars). A window
   * computed from `bars.length` therefore landed 5 slots short on the right - a 4 pct gutter where
   * R2 rules 8. The newest candle's OWN time-scale index + 1 is the count the window must end on;
   * `bars.length` is the fall-through only when the chart cannot resolve that time.
   */
  function landingCount(bars: readonly { t: number }[]): number {
    const newest = bars.length ? bars[bars.length - 1].t : null;
    const index = newest === null ? null : chart?.timeScale().timeToIndex(secs(newest), true) ?? null;
    // Published so a census can grade the anchor it landed on, not infer it: count:index:bars.
    if (host) host.dataset.landing = `${index === null ? bars.length : Number(index) + 1}:${index ?? "null"}:${bars.length}`;
    return index === null ? bars.length : Number(index) + 1;
  }

  /**
   * R2 + R3 ON THE COCKPIT: the window lands on the MEASURED plot, and a resize keeps the follow
   * anchor with the same gutter share.
   *
   * ⛔ THE FIRST FEED RUNS BEFORE LAYOUT. `feedSeries()` is called right after the chart is created
   * with autoSize, when `timeScale().width()` still reads 0, so the first landing falls through to
   * the fixed default and nothing re-landed once the plot was measured: Chart B at 12m sat on the
   * library's default with a ZERO gutter until the next 12m bar (HUDCHAT-38's 90 s series, 18:47Z
   * 09-24). So a resize re-lands while FOLLOWING. INSPECT is left alone (R3 keeps the interval), and
   * so is the focus mount, whose persisted range is restored by its own branch.
   */
  function relandOnMeasuredPlot() {
    if (!chart || focusHeight !== null || candles.length === 0 || fedLast === null) return;
    const visible = chart.timeScale().getVisibleRange();
    if (visible !== null && !coversLiveEdge(Number(visible.to), fedLast)) return;
    chart.timeScale().setVisibleLogicalRange(landingRangeFor(landingCount(candles)));
  }

  function landingRangeFor(barCount: number) {
    const plotPx = chart?.timeScale().width() ?? 0;
    const rung = rungFromKey(timeframe);
    const tf = rung !== null && rung.kind === "tf" ? String(rung.tf) : null;
    const wanted = tf === null || !Number.isFinite(plotPx) || plotPx <= 0
      ? defaultFocusLogicalRange(barCount)
      : landingLogicalRange(barCount, plotPx, tf, PANE_GUTTER_FRAC);
    // Published beside data-landing so a census grades what was ASKED against what the chart shows.
    if (host) host.dataset.landingWanted = `${wanted.from}:${wanted.to}@${Math.round(plotPx)}px`;
    return wanted;
  }

  function resetFocusZoom() {
    if (focusHeight === null || focusRungKey === null || chart === null || candles.length === 0) return;
    writeFocusRange(focusRungKey, null);
    const wanted = landingRangeFor(landingCount(candles));
    chart.timeScale().setVisibleLogicalRange(wanted);
  }
  /** Publish one native chart range under every named pane; all three move as one stack. */
  function publishVisibleRanges() {
    if (!chart || !host) return;
    const logical = chart.timeScale().getVisibleLogicalRange();
    const encodedLogical = logical === null ? "" : `${logical.from}:${logical.to}`;
    host.dataset.pane0Range = encodedLogical;
    host.dataset.pane2Range = encodedLogical;
    host.dataset.paneRanges = JSON.stringify(chart.panes().map(() => encodedLogical));
    host.dataset.paneHeights = JSON.stringify(chart.panes().map((pane) => pane.getHeight()));
    host.dataset.paneCount = String(chart.panes().length);
    const visible = chart.timeScale().getVisibleRange();
    host.dataset.visibleTimeRange = visible === null ? "" : `${Number(visible.from)}:${Number(visible.to)}`;
    const nextWindow = visible === null
      ? null
      : { from: Number(visible.from) * 1_000, to: Number(visible.to) * 1_000 };
    const encodedWindow = nextWindow === null ? "" : `${nextWindow.from}:${nextWindow.to}`;
    if (onVisibleWindowChange && encodedWindow !== publishedWindow) {
      publishedWindow = encodedWindow;
      onVisibleWindowChange(nextWindow);
    }
    refreshWindowShade();
  }

  /**
   * The boundary is the calendar's, never a UTC hour spelled into this pane (session.ts is the authority).
   *
   * ⛔ THIS LINE WAS `Math.max(...bars.map(b => b.t).filter(Number.isFinite))` AND IT IS THE
   * `-Infinity` R650's S4b names. It is safe TODAY only by luck of which route feeds it — driven
   * against both live routes, 2026-08-24 18:5xZ:
   * ```
   *   /api/es/cvd?tf=1     t: number 1787597340000   ⇒ fine
   *   /api/es/cvd/submin   ** NO `t` AT ALL **       ⇒ every bar filtered out
   *                        ⇒ Math.max(...[]) === ** -Infinity **, silently, no chip
   * ```
   * ⇒ ***S4b's OWN JOB — pointing this pane at the sub-minute route — IS WHAT DETONATES IT.*** The
   * guard `bars.length ?` protects the EMPTY case and not this one: bars exist, none is readable.
   *
   * ⭐ AND IT CURES GK-ae's J3 (18:47Z) STRUCTURALLY. ae deleted this line's `.filter(Number.isFinite)`
   * and the wire arm stayed **10/10 GREEN**, because that arm RE-TYPES the predicate instead of
   * executing it — *"a copy of a contract is not a check of it."* Calling the shared reader means
   * `rungBarTime.test.ts` now arms THIS line, not a lookalike of it.
   */
  let timing = $derived(timeBinding(bars));

  /**
   * ⛔⛔ NORMALISE THE CLOCK ONCE, AT THE PANE'S EDGE — `codex review` [P1], DRIVEN AND CONFIRMED.
   *
   * Curing `lastBarMs` alone fixed the session boundary and NOTHING ELSE, because every downstream
   * helper reads `b.t` for itself (`cvd2.ts:126,145,226,238`). Driven against the real pipeline
   * with the deployed sub-minute shape:
   * ```
   *   SUBMIN (bar_open, no `t`)   cvdCandles -> first.t === ** undefined **  ⇒ secs(undefined) -> NaN
   *   1m     (t present)          cvdCandles -> first.t === 1787596740000    ⇒ fine
   * ```
   * ⇒ ***THE CHIP WOULD HAVE SAID "CLOCK DERIVED FROM bar_open" OVER A PANE THAT DREW NOTHING.***
   * That is not a partial fix, it is the S3 law broken again one slice later: **a legend must not
   * lie about its own chart.** Advertising a fallback the renderer cannot honour is the same defect
   * as drawing two series in one colour under a two-colour legend.
   *
   * ⭐ ONE ADAPTATION, AT THE BOUNDARY, SO NOTHING DOWNSTREAM RE-JUDGES THE CLOCK — the same rule
   * `bars` already states one line up: *"nothing downstream re-judges the feed."* Bars that name no
   * readable instant are DROPPED here rather than passed on as `undefined`, and `timing.unreadable`
   * above is what makes that drop visible instead of silent.
   */
  let timedBars = $derived(
    bars.flatMap((b) => {
      const t = barTimeMs(b);
      return t === null ? [] : [{ ...b, t }];
    }),
  );
  let lastBarMs = $derived(barTimeExtent(bars)?.max ?? null);

  /**
   * ⛔ LAW 1 WANTS BOTH HALVES: *"no degraded modes without a visible STALE/ERROR state AND A LOG
   * LINE."* `codex review` [P2] — I had shipped the chips and stopped, which is the visible half
   * only. The chip serves the owner; the log serves whoever is asked why a chart looked odd an
   * hour ago, and a chip that has already been re-rendered away answers nobody.
   * ⚠ KEYED ON THE VERDICT, NOT ON THE RENDER. `$effect` re-runs on every dependency change, and a
   * line printed per frame is not a log — it is noise that trains the reader to filter the channel.
   * Only TRANSITIONS are logged, and the healthy state is logged as a recovery rather than in
   * silence, so the absence of a line never has to be interpreted.
   */
  let loggedClock: string | null = $state(null);
  $effect(() => {
    if (!bars.length) return;
    const key = `${timing.source}:${timing.preCure}:${timing.unreadable}/${timing.total}`;
    if (key === loggedClock) return;
    loggedClock = key;
    if (timing.source === "none") {
      console.error(
        `[SessionCvdPane] NO READABLE BAR TIME — ${timing.total} bars served, none carries \`t\` ` +
          `or a zoned \`bar_open\`. CVD pane cannot be timed; drawing nothing rather than guessing.`,
      );
    } else if (timing.source === "bar_open") {
      console.warn(
        `[SessionCvdPane] clock DERIVED from \`bar_open\` for ${timing.resolved}/${timing.total} bars` +
          `${timing.preCure ? " — route predates the `t` cure (R649)" : " — some bars lack `t`"}.` +
          `${timing.unreadable ? ` ${timing.unreadable} bar(s) dropped as untimeable.` : ""}`,
      );
    } else {
      console.info(`[SessionCvdPane] clock from \`t\` for all ${timing.total} bars.`);
    }
  });

  let boundaryMs = $derived.by(() => {
    if (lastBarMs === null) return null;
    const spans = sessionSegments(lastBarMs - SESSION_LOOKBACK_MS, lastBarMs + 1);
    const opens = spans.filter((s) => !s.closed && s.from <= lastBarMs);
    return opens.length ? opens[opens.length - 1].from : null;
  });
  let cvdModel = $derived(buildCvdStudyModel(timedBars, boundaryMs, cvdMode));
  let cvdGapOverlay = $derived(cvdGapMarkers(timedBars, cvdBarSpanMs));
  /** R1707: CONT passes literal zero; SESSION spends the exact session-open anchor once. */
  let cvdCandleModel = $derived(buildCvdCandleModel(
    timedBars,
    cvdMode === "session" ? cvdModel.anchor?.anchorValue ?? 0 : 0,
    cvdModel.blocked,
    cvdMode,
  ));
  let intensitySessionSpans = $derived.by(() => {
    if (!timedBars.length) return [];
    return sessionSegments(timedBars[0].t - 1, timedBars[timedBars.length - 1].t + 1)
      .filter((span) => !span.closed);
  });
  const sessionKeyAt = (t: number) => {
    const span = intensitySessionSpans.find((candidate) => candidate.from <= t && t < candidate.to);
    return span ? String(span.from) : `OUTSIDE-ES:${rthSessionAt(t)?.key ?? "unknown"}`;
  };
  const rthKeyAt = (t: number) => rthSessionAt(t)?.key ?? null;
  let completeRthSessions = $derived.by(() => {
    const minutes = new Map<string, Map<number, number>>();
    for (const point of cvdModel.delta) {
      const rth = rthSessionAt(point.t);
      if (!rth) continue;
      const group = minutes.get(rth.key) ?? new Map<number, number>();
      group.set(point.t, rth.minute);
      minutes.set(rth.key, group);
    }
    const spanMinutes = Math.max(1, cvdBarSpanMs / 60_000);
    return new Set([...minutes.entries()]
      .filter(([, group]) => {
        const points = [...group.entries()].sort(([a], [b]) => a - b);
        if (!points.length) return false;
        const firstMinute = points[0][1];
        const lastMinute = points.at(-1)![1];
        const continuous = points.every(([t], index) => index === 0 || t - points[index - 1][0] === cvdBarSpanMs);
        return firstMinute <= 9 * 60 + 30 && lastMinute + spanMinutes >= 16 * 60 && continuous;
      })
      .map(([key]) => key));
  });
  let cvdIntensity = $derived(buildCvdIntensity(cvdModel.delta.map((point) => ({
    t: point.t,
    delta: point.value,
    session: sessionKeyAt(point.t),
    rthSession: rthKeyAt(point.t),
  })), null, completeRthSessions));
  let markerEligible = $derived(chartRole === "lower" && !timeframe.startsWith("s:"));
  let intensityHasPreviousFloor = $derived(cvdIntensity.some((bar) => bar.previousFloorAvailable));
  let cvdMarkers = $derived(markerEligible ? detectCvdMarkers(
    cvdIntensity,
    candles.map((candle) => ({
      t: candle.t,
      h: candle.h,
      l: candle.l,
      session: sessionKeyAt(candle.t),
      rthSession: rthKeyAt(candle.t),
    })), null, completeRthSessions,
  ) : []);
  let rthBarCounts = $derived.by(() => {
    const counts = new Map<string, number>();
    for (const candle of candles) { const key = rthSessionAt(candle.t)?.key; if (key) counts.set(key, (counts.get(key) ?? 0) + 1); }
    return counts;
  });
  let exhaustionLows = $derived.by(() => {
    if (timeframe !== "1") return [];
    return detectExhaustionLows(candles.flatMap((candle) => {
    const delta = cvdModel.delta.find((point) => point.t === candle.t)?.value;
    const rthSession = rthSessionAt(candle.t);
    if (rthSession === null || (rthBarCounts.get(rthSession.key) ?? 0) < 390) return [];
    return typeof delta === "number" ? [{
      t: candle.t, delta, priceHigh: candle.h, priceLow: candle.l, close: candle.c,
      session: rthSession.key,
    }] : [];
    }));
  });
  let absorptionCount = $derived(cvdMarkers.filter((marker) => marker.kind === "ABS").length);
  let exhaustionCount = $derived(exhaustionLows.length);
  /** R4566: a FILLED minute has its bar and is never residue; the RESIDUE header counts ABSENT minutes only. */
  let residueAbsent = $derived.by(() => {
    return declaredResidue.filter((entry) => entry.state !== "FILLED");
  });
  let markerGapCount = $derived.by(() => {
    if (!markerEligible) return 0;
    const candleTimes = new Set(candles.map((candle) => candle.t));
    const declaredTimes = new Set(declaredResidue.map((entry) => Date.parse(entry.minute)));
    return cvdIntensity.filter((bar) => !candleTimes.has(bar.t) && !declaredTimes.has(bar.t)).length;
  });
  let markerGapTimestamp = $derived.by(() => {
    if (markerGapCount === 0) return null;
    const candleTimes = new Set(candles.map((candle) => candle.t));
    const declaredTimes = new Set(declaredResidue.map((entry) => Date.parse(entry.minute)));
    return cvdIntensity.find((bar) => !candleTimes.has(bar.t) && !declaredTimes.has(bar.t))?.t ?? null;
  });
  let markerGapPresentation = $derived(markerGapState(markerGapCount, markerGapTimestamp, venueClosed, servedVenueKnown));
  let loggedMarkerGapState: string | null = $state(null);
  $effect(() => {
    const key = `${servedVenueKnown ? (venueClosed ? "CLOSED" : "LIVE") : "UNKNOWN"}:${markerGapCount}`;
    if (!markerEligible || key === loggedMarkerGapState) return;
    loggedMarkerGapState = key;
    if (markerGapPresentation?.logError) {
      console.error(`[SessionCvdPane] MARKERS ERROR — ${markerGapCount} CVD bucket(s) lack same-time candles; ABS?/EXH? withheld across those gaps.`);
    } else if (markerGapCount === 0) {
      console.info("[SessionCvdPane] marker candle join recovered — ABS?/EXH? evaluation complete for the served window.");
    }
  });
  let divergences = $derived.by(() => {
    if (chartRole !== "higher") return [];
    const candleByTime = new Map(candles.map((candle) => [candle.t, candle]));
    const deltaByTime = new Map(cvdModel.delta.map((point) => [point.t, point.value]));
    const pairs = cvdModel.segments.flatMap((segment) => {
      const groups: CvdDivergenceInput[][] = [];
      let group: CvdDivergenceInput[] = [];
      let groupKey: string | null = null;
      const flush = () => {
        if (group.length) groups.push(group);
        group = [];
        groupKey = null;
      };
      for (const point of segment.points) {
        const candle = candleByTime.get(point.t);
        if (point.value === null || !candle) {
          flush();
          continue;
        }
        const key = sessionKeyAt(point.t);
        if (groupKey !== null && key !== groupKey) flush();
        groupKey = key;
        group.push({
          t: point.t,
          cvd: point.value,
          priceHigh: candle.h,
          priceLow: candle.l,
          delta: deltaByTime.get(point.t) ?? 0,
        });
      }
      flush();
      return groups.flatMap((inputs) => timeframe === "1"
        ? detectCalibratedAbsorptions(inputs)
        : detectCvdDivergences(inputs, timeframe));
    });
    const tfMinutes = Number(timeframe);
    return pairs
      .filter((pair) => Number.isFinite(tfMinutes) && pair.confirmedAt >= pair.swingAt + pair.w * tfMinutes * 60_000)
      .slice(-4)
      .map((pair, id) => ({ ...pair, id }));
  });
  let divergenceSupported = $derived(divergenceWindow(timeframe) !== null);
  let loggedDivergenceSupport: string | null = $state(null);
  $effect(() => {
    if (chartRole !== "higher") return;
    const state = divergenceSupported ? `supported:${timeframe}` : `unsupported:${timeframe}`;
    if (state === loggedDivergenceSupport) return;
    loggedDivergenceSupport = state;
    if (divergenceSupported) console.info(`[SessionCvdPane] CVD divergence study active at ${timeframe}m.`);
    else console.warn(`[SessionCvdPane] CVD DIVERGENCE UNAVAILABLE — rung ${timeframe} is unstudied; drawing no pairs.`);
  });
  let anchor = $derived(cvdModel.anchor);
  let anchored = $derived(anchor !== null);
  let cvdNoData = $derived(cvdBlocked === null && cvdModel.noData);
  let cvdPaneBlocked = $derived(cvdBlocked ?? cvdModel.blocked ?? cvdCandleModel.blocked);
  let cvdError = $derived(cvdNoData ? null : cvdPaneBlocked);
  let cvdTransform = $derived(cvdMode === "session"
    ? anchorNote(cvdFeed, anchor)
    : "CONT draws the worker's served CVD spine unchanged; no client anchor offset");
  /**
   * ⛔⛔ ORDER 77 / R1303 — THE ANCHOR STATE GOES ON THE INK, NOT ONLY INTO A NOTE.
   * `anchorNote()` has always described the transform correctly, and the owner never saw it: it is
   * a string in a data attribute. The desk read the 1m pane at ~248k and the 12m pane at ~0 as a
   * DATA fault and spent an eyes-on, a ruling and two seats on it — because the one fact that
   * explained it was not rendered anywhere a reader looks.
   * ⇒ *** A STATE THE PANE KNOWS AND DOES NOT DRAW IS A STATE THE OWNER WILL DIAGNOSE AS DATA LOSS. ***
   * This reads the SERVER's claim (`session_key` on the bar) and never re-derives it.
   */
  let servedAnchor = $derived(servedAnchorState(cvdFeed?.bars ?? []));
  let cvdStudy = $derived(studyChip(cvdFeed) ?? "HYPOTHESIS");
  let cvdProvenance = $derived(
    `CVD · SOURCE tick · SIGNING vendor_aggressor · DISPLAY ${cvdMode.toUpperCase()} · ACCUMULATOR CONTINUOUS`,
  );
  let loggedStudyState = $state<string | null>(null);
  $effect(() => {
    // ⛔ THE SERVED state, not the client's. The client no longer computes an anchor, so keying
    // this on `anchor === null` would print "NO SESSION ANCHOR" on every load of a CORRECTLY
    // anchored pane — the exact line ORDER 77's done-test requires to be absent.
    const state = cvdNoData ? "NO DATA" : cvdPaneBlocked ?? (servedAnchor.state === "UNANCHORED" ? "NO SESSION ANCHOR" : "LIVE");
    if (state === loggedStudyState) return;
    loggedStudyState = state;
    if (cvdNoData) console.warn(`[SessionCvdPane] CVD NO DATA — ${cvdModel.blocked}`);
    else if (cvdPaneBlocked) console.error(`[SessionCvdPane] CVD ERROR — ${cvdPaneBlocked}`);
    else if (servedAnchor.state === "UNANCHORED") console.warn("[SessionCvdPane] NO SESSION ANCHOR — the server did not carry session_key; drawing the series AS SERVED, never re-basing here.");
    else console.info(`[SessionCvdPane] CVD recovered — ${cvdModel.segments.length} instrument segment(s).`);
  });

  const secs = (ms: number) => Math.floor(ms / 1000) as UTCTimestamp;

  function refreshWindowShade() {
    if (chartRole !== "lower" || !chart || !activeWindow) {
      windowShade = null;
      return;
    }
    const visible = chart.timeScale().getVisibleRange();
    if (!visible) { windowShade = null; return; }
    const visibleFrom = Number(visible.from) * 1_000;
    const visibleTo = Number(visible.to) * 1_000;
    if (activeWindow.from > visibleFrom || activeWindow.to < visibleTo) {
      windowShade = null;
      return;
    }
    const left = chart.timeScale().timeToCoordinate(secs(visibleFrom));
    const right = chart.timeScale().timeToCoordinate(secs(visibleTo));
    windowShade = left === null || right === null ? null : { left, width: Math.max(0, right - left) };
  }

  $effect(() => {
    void activeWindow;
    refreshWindowShade();
  });

  let levelTokens = $state({ magnet: "", flip: "" });
  let levelLines: IPriceLine[] = [];
  let offFrame = $state(0);
  /**
   * EDGE TAGS — the design board's Option C, ADOPTED 08-09 and never built until now.
   * ⛔ OPTION A ("draw only what fits") WAS REJECTED IN WRITING for shipping a visibly unchanged
   * chart while omitting every level the feature exists to show, and OPTION B (auto-fit the range)
   * for squashing 29.5 points of candles to display a 356-point wall — *the chart's first job is
   * the price.*
   * ⚠ AND OFF-FRAME IS THE NORMAL CASE, NOT AN EDGE CASE: at the owner's default 1m rung the candle
   * window is ~16 points and the walls sit ±15-20 away. Measured 20:0xZ — 2 of 3 levels off-frame.
   * ⇒ ***A COUNT SAYS "SOMETHING IS MISSING"; A TAG SAYS WHICH, WHICH WAY, AND HOW FAR.***
   */
  type EdgeTag = { side: "above" | "below"; cls: "magnet" | "gammaflip"; text: string };
  type InFrameLevel = { px: number; cls: "magnet" | "gammaflip"; text: string };
  type InFrameTag = InFrameLevel & { top: number };
  let edgeTags = $state<EdgeTag[]>([]);
  let inFrameTags = $state<InFrameTag[]>([]);
  let inFrameLevels: InFrameLevel[] = [];
  let pane0Height = $state(0);
  let levelPositionErrors = $state(0);
  let levelPositionFrame: number | null = null;
  const LEVEL_BADGE_HEIGHT = 15;
  const LEVEL_BOUNDARY_GAP = 6;

  function readPane0Height(): number {
    const measured = chart?.panes()[0]?.getHeight() ?? 0;
    pane0Height = measured;
    if (host) host.dataset.pane0Height = String(measured);
    return measured;
  }

  function refreshInFrameTagPositions() {
    levelPositionFrame = null;
    if (!priceSeries) {
      inFrameTags = [];
      levelPositionErrors = inFrameLevels.length;
      return;
    }
    const paneH = readPane0Height();
    const positioned: InFrameTag[] = [];
    let positionErrors = 0;
    for (const tag of inFrameLevels) {
      const px = tag.px;
      const y = priceSeries.priceToCoordinate(px);
      if (y === null || paneH <= 0) {
        positionErrors += 1;
        continue;
      }
      const half = LEVEL_BADGE_HEIGHT / 2;
      const minY = LEVEL_BOUNDARY_GAP + half;
      const maxY = Math.max(minY, paneH - LEVEL_BOUNDARY_GAP - half);
      positioned.push({ ...tag, top: Math.min(maxY, Math.max(minY, y)) });
    }
    inFrameTags = positioned;
    levelPositionErrors = positionErrors;
  }

  function scheduleLevelPositionRefresh() {
    if (levelPositionFrame !== null) return;
    levelPositionFrame = requestAnimationFrame(refreshInFrameTagPositions);
  }

  /**
   * ⛔⛔ ACCUMULATE INTO A LOCAL, WRITE THE $state ONCE — inherited verbatim from `LwcPriceCanvas`,
   * where `offFrame += 1` was a READ **and** a WRITE of a rune inside the very effect that calls
   * this: the effect becomes its own dependency, Svelte aborts the update, and THE WHOLE COCKPIT
   * FREEZES with the renderer on.
   * ⚠ AND IT ONLY FIRES WHEN A LEVEL IS ACTUALLY OFF-FRAME, so every hermetic gate stayed green —
   * fixtures keep levels in range and the branch never runs.
   * ⇒ ***A SELF-DEPENDENT EFFECT CAN HIDE BEHIND A BRANCH THAT ONLY LIVE DATA TAKES.***
   * I did not rediscover this; I read it in the source I ported from. That is the whole argument
   * for porting rather than rewriting.
   */
  function pushLevels() {
    for (const line of levelLines) priceSeries?.removePriceLine(line);
    levelLines = [];
    let off = 0;
    const tags: EdgeTag[] = [];
    const inside: InFrameLevel[] = [];
    if (!showLevels || !priceSeries || !candles.length) {
      inFrameLevels = [];
      offFrame = 0; edgeTags = []; inFrameTags = []; levelPositionErrors = 0; return;
    }
    // The frame is what the candles span; a level outside it cannot be drawn at a true position.
    let lo = Infinity, hi = -Infinity;
    for (const c of candles) { if (c.l < lo) lo = c.l; if (c.h > hi) hi = c.h; }
    for (const lv of levels ?? []) {
      const px = lv.px;
      if (typeof px !== "number" || !Number.isFinite(px)) continue;
      // ⛔ COUNTED, NEVER DROPPED. A level the frame cannot show is still a fact about the book, and
      // a renderer that silently omits it tells the reader the book is emptier than it is.
      if (px < lo || px > hi) {
        off += 1;
        // The SAME grammar as an on-canvas level — same colour, same label — so it reads as the
        // line continuing past the frame rather than as an annotation about a line. Direction and
        // distance are the whole of what an off-screen level can usefully say.
        tags.push({
          side: px > hi ? "above" : "below",
          cls: lv.type === "gammaflip" ? "gammaflip" : "magnet",
          text: lv.label ?? String(px),
        });
        continue;
      }
      levelLines.push(
        priceSeries.createPriceLine({
          price: px,
          color: lv.type === "gammaflip" ? levelTokens.flip : levelTokens.magnet,
          lineWidth: 1,
          // The flip is DASHED and the magnets SOLID — the encoding the design board ratified and
          // the legend promises. A renderer that draws them alike makes the legend a lie.
          lineStyle: lv.type === "gammaflip" ? LineStyle.Dashed : LineStyle.Solid,
          // LWC's native badge is clipped to pane 0, but its lower edge sits only 1px from pane 1's
          // axis canvas in the three-pane layout. Keep LWC's line; render its text in the clamped
          // pane-0 overlay below, where the bounding box is measurable and cannot touch pane 1.
          axisLabelVisible: false,
          title: "",
        }),
      );
      inside.push({
        px,
        cls: lv.type === "gammaflip" ? "gammaflip" : "magnet",
        text: lv.label ?? String(px),
      });
    }
    // ⛔ ONE WRITE EACH, UNCONDITIONAL, AND NEITHER IS READ IN HERE — the self-dependent-effect
    // cure this file inherited from `LwcPriceCanvas`, extended to the tag list.
    offFrame = off;
    edgeTags = tags;
    inFrameLevels = inside;
    refreshInFrameTagPositions();
  }

  let loggedLevelPositionErrors: number | null = $state(null);
  $effect(() => {
    if (levelPositionErrors === loggedLevelPositionErrors) return;
    loggedLevelPositionErrors = levelPositionErrors;
    if (levelPositionErrors > 0) {
      console.error(
        `[SessionCvdPane] LEVEL POSITION ERROR — ${levelPositionErrors} in-frame dealer ` +
          "badge(s) had no pane-0 price coordinate; text withheld rather than drawn on another pane.",
      );
    } else if (levels.length) {
      console.info("[SessionCvdPane] dealer level badges positioned inside pane 0.");
    }
  });

  $effect(() => {
    // Named dependencies, so a levels change redraws even though this pane sets its DATA only on
    // mount: the overlay must not inherit that lifecycle.
    void levels; void showLevels; void levelTokens; void candles;
    pushLevels();
  });

  function pinPaneHeights() {
    if (!chart || !host) return;
    const panes = chart.panes();
    if (focusHeight !== null) {
      const studyHeight = focusHeight - PANE_MIN_HEIGHTS.vsplit;
      panes[PRICE_PANE]?.setStretchFactor(studyHeight * FOCUS_PROPORTIONS.chart.priceFraction);
      panes[CVD_LINE_PANE]?.setStretchFactor(studyHeight * FOCUS_PROPORTIONS.chart.cvdFraction);
      panes[VSPLIT_PANE]?.setStretchFactor(PANE_MIN_HEIGHTS.vsplit);
      host.dataset.paneHeights = JSON.stringify(panes.map((pane) => pane.getHeight()));
      readPane0Height();
      return;
    }
    const landing = landingPaneHeights(height);
    // A hidden default chart can report 1px before layout. Keep its last split; ResizeObserver retries.
    if (landing === null) return;
    // R2010 / PANE-CALIBRATION-1 34eb4e6e: every landing host uses the declared proportional split.
    const factors = [landing.price, landing.cvdLine, landing.vsplit];
    for (let index = 0; index < factors.length; index += 1) panes[index]?.setStretchFactor(factors[index]);
    host.dataset.paneHeights = JSON.stringify(panes.map((pane) => pane.getHeight()));
    readPane0Height();
  }

  function alpha(color: string, opacity: number): string {
    const hex = /^#([0-9a-f]{6})$/i.exec(color);
    if (!hex) return color;
    const channel = Math.round(Math.max(0, Math.min(1, opacity)) * 255).toString(16).padStart(2, "0");
    return `${color}${channel}`;
  }

  function clearDivergenceSeries() {
    if (!chart) return;
    for (const entry of divergenceSeries) chart.removeSeries(entry.series);
    divergenceSeries = [];
  }

  let ghostSegments = $derived.by(() => {
    // G2 is calibrated on the 1m tape; an unstudied rung must stay display-only and carry no ghost.
    if (timeframe !== "1") return [];
    const cvdByTime = new Map(cvdModel.segments.flatMap((segment) =>
      segment.points.filter((point): point is { t: number; value: number } => point.value !== null)
        .map((point) => [point.t, point.value] as const)));
    const group = candles.flatMap((candle) => {
      if (rthSessionAt(candle.t) === null) return [];
      const cvdValue = cvdByTime.get(candle.t);
      return typeof cvdValue === "number" ? [{ t: candle.t, price: candle.c, cvd: cvdValue, session: sessionKeyAt(candle.t) }] : [];
    });
    // Admission is bounded by the wall snapshot known strictly before at least one candidate;
    // buildGhostSegments applies the per-candidate strict bound again, preserving PIT semantics.
    const knownWalls = ghostWalls.filter((wall) => group.some((_, i) => wall.availableAt < group[i].t));
    return buildGhostSegments(group, knownWalls);
  });
  let renderedGhostSegments = $derived(ghostSegments.slice(-4));

  function clearGhostSeries() {
    if (!chart) return;
    for (const entry of ghostSeries) chart.removeSeries(entry.series);
    ghostSeries = [];
  }

  function syncGhostSeries(t: ResolvedTokens) {
    if (!chart) return;
    clearGhostSeries();
    for (const segment of renderedGhostSegments) {
      const color = segment.className === "amber" ? t["--magnet"] : t["--flip"];
      const series = chart.addSeries(LineSeries, {
        // GHOST-INK-1 hop 1: the axis pill reads what the detector measured, never the internal id.
        title: ghostAxisTitle(segment),
        color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      }, PRICE_PANE);
      series.setData(segment.points.map((point, index) => ({
        time: secs(point.t), value: point.value,
        color: alpha(color, Math.min(
          .25 + .75 * ((index + 1) / Math.max(1, segment.points.length)),
          segment.resolvedAt === null
            ? 1
            : .25 + .75 * ((segment.points.length - index) / Math.max(1, segment.points.length)),
        )),
      })));
      ghostSeries.push({ id: segment.id, segment, series });
    }
  }

  function syncDivergenceSeries(t: ResolvedTokens) {
    if (!chart) return;
    clearDivergenceSeries();
    for (const pair of divergences) {
      const priceId = `cvdDivPrice:${pair.id}`;
      const cvdId = `cvdDivCvd:${pair.id}`;
      const common = {
        color: t["--flip"],
        lineWidth: 1 as const,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      };
      const priceLine = chart.addSeries(LineSeries, { ...common, title: "" }, PRICE_PANE);
      priceLine.setData([
        { time: secs(pair.previousSwingAt), value: pair.previousPrice },
        { time: secs(pair.swingAt), value: pair.currentPrice },
      ]);
      divergenceSeries.push({ id: priceId, pair, series: priceLine });
      const cvdLine = chart.addSeries(LineSeries, { ...common, title: "", priceScaleId: "right" }, CVD_LINE_PANE);
      cvdLine.setData([
        { time: secs(pair.previousSwingAt), value: pair.previousCvd },
        { time: secs(pair.swingAt), value: pair.currentCvd },
      ]);
      divergenceSeries.push({ id: cvdId, pair, series: cvdLine });
    }
    divergencePriceMarkers?.setMarkers([
      ...divergences.map((pair) => ({
        time: secs(pair.confirmedAt), position: "aboveBar" as const, shape: "circle" as const,
        color: t["--magnet"], text: "ABS?", size: 1,
      })),
      ...exhaustionLows.map((mark) => ({
        time: secs(mark.t), position: "belowBar" as const, shape: "circle" as const,
        color: t["--flip"],
        text: `EXH? ${formatContracts(mark.pushContractsPerTick)}c/t vs ${formatContracts(mark.legContractsPerTick)}c/t`,
        size: 1,
      })),
      ...renderedGhostSegments.flatMap((ghost) => ghost.className === "violet" && ghost.resolvedAt !== null ? [{
        time: secs(ghost.resolvedAt), position: "aboveBar" as const, shape: "circle" as const,
        color: t["--flip"], text: "◆ SNAP", size: 1,
      }] : []),
    ]);
    divergenceCvdMarkers.forEach((markers, index) => markers.setMarkers([
      ...divergences.flatMap((pair) => {
        const segment = cvdCandleSeries?.data() ?? [];
        return segment.some((point) => Number(point.time) === secs(pair.swingAt)) ? [{
          time: secs(pair.confirmedAt), position: "inBar" as const, shape: "circle" as const,
          color: t["--magnet"], text: "ABS?", size: 1,
        }] : [];
      }),
      ...exhaustionLows.flatMap((mark) => {
        const segment = cvdCandleSeries?.data() ?? [];
        return segment.some((point) => Number(point.time) === secs(mark.t)) ? [{
          time: secs(mark.t), position: "belowBar" as const, shape: "circle" as const,
          color: t["--flip"], text: "EXH?", size: 1,
        }] : [];
      }),
    ]));
    syncGhostSeries(t);
  }

  function updateSessionZero(t: ResolvedTokens) {
    const owner = cvdCandleSeries;
    if (zeroLine && owner) owner.removePriceLine(zeroLine);
    zeroLine = null;
    if (anchored && owner) {
      zeroLine = owner.createPriceLine({
        price: 0,
        color: t["--ink"],
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: "session open",
      });
    }
  }

  function paint(t: ResolvedTokens) {
    if (!chart || !priceSeries || !host) return;
    // Chain 143's token bridge: one option set for landing and focus, dark BRUCY held (ZOOM-LANE-1 arm 1 pins
    // this exact call). LWC merges option objects, so the font size set at creation survives the repaint.
    chart.applyOptions(sessionChartOptions(t, focusHeight === null ? "landing" : "focus"));
    // Chain 144: the native chart font is 12 px × --ink-scale, re-measured on the host at every repaint.
    chart.applyOptions({ layout: { fontSize: chartFontSize(host) } });
    levelTokens = { magnet: t["--magnet"], flip: t["--flip"] };
    priceSeries.applyOptions({ upColor: t["--up"], downColor: t["--dn"], borderVisible: false,
      wickUpColor: t["--up"], wickDownColor: t["--dn"] });
    cvdCandleSeries?.applyOptions({
      title: "",
      upColor: t["--cvd-pos"], downColor: t["--cvd-neg"],
      wickUpColor: t["--cvd-pos"], wickDownColor: t["--cvd-neg"],
      borderVisible: false, priceLineVisible: false, lastValueVisible: true,
    });
    fastSeries?.applyOptions({ color: t["--flip"], lineWidth: 1, priceLineVisible: false });
    slowSeries?.applyOptions({ color: t["--ink"], lineWidth: 2, priceLineVisible: false });
    updateSessionZero(t);
    feedSeries();
  }

  function chartFontSize(element: HTMLElement): number {
    const raw = getComputedStyle(element).getPropertyValue("--ink-scale").trim();
    const scale = Number(raw);
    if (!Number.isFinite(scale) || scale <= 0) {
      throw new Error(`SessionCvdPane: --ink-scale must resolve to a finite positive number; received ${JSON.stringify(raw)}`);
    }
    return 12 * scale;
  }

  onMount(() => {
    if (!host) return;
    const unsubscribeServedVenueState = subscribeServedVenueState((state, known) => {
      servedVenueState = state;
      servedVenueKnown = known;
    });
    cvdMode = displayMode ?? storedCvdMode();
    const syncUncontrolledMode = (event: Event) => {
      if (displayMode === null) cvdMode = (event as CustomEvent<CvdDisplayMode>).detail;
    };
    window.addEventListener(CVD_MODE_EVENT, syncUncontrolledMode);
    tokens = resolveTokens(host);
    chart = createChart(host, {
      autoSize: true,
      // Chain 143's token bridge: one option set for landing and focus (ZOOM-LANE-1 arm 1 pins this spread).
      ...sessionChartOptions(tokens, focusHeight === null ? "landing" : "focus"),
    });
    // Chain 144's measured native-chart font, 12 px × --ink-scale (LANDING-SCALE-1 arm 2 pins this literal),
    // applied before the first series is added.
    chart.applyOptions({ layout: { attributionLogo: false, fontSize: chartFontSize(host) } });
    priceSeries = chart.addSeries(CandlestickSeries, {}, PRICE_PANE);
    // R3090 (1036f30d box 0): declared headroom above and below the last print, never the library's 0.2/0.1.
    priceSeries.priceScale().applyOptions({ scaleMargins: { top: PRICE_PANE_MARGIN_FRAC, bottom: PRICE_PANE_MARGIN_FRAC } });
    divergencePriceMarkers = createSeriesMarkers(priceSeries, []);
    // ORDER 35-S2: native cumulative-delta OHLC replaces the retired cumulative line drawable.
    cvdCandleSeries = chart.addSeries(CandlestickSeries, cvdCandleOptions(), CVD_LINE_PANE);
    nativeCvdOwners.add(cvdCandleSeries);
    cvdPivotMarkers = [createSeriesMarkers(cvdCandleSeries, [])];
    cvdGapMarkerLayer = createSeriesMarkers(cvdCandleSeries, []);
    divergenceCvdMarkers = [createSeriesMarkers(cvdCandleSeries, [])];
    fastSeries = chart.addSeries(LineSeries, {}, VSPLIT_PANE);
    slowSeries = chart.addSeries(LineSeries, {}, VSPLIT_PANE);
    fastSeries.priceScale().applyOptions({ scaleMargins: { top: 0.08, bottom: 0.08 } });
    feedSeries();
    pinPaneHeights();
    refreshInFrameTagPositions();
    const resizeObserver = new ResizeObserver(() => {
      pinPaneHeights();
      relandOnMeasuredPlot();
      refreshInFrameTagPositions();
      publishVisibleRanges();
    });
    resizeObserver.observe(host);
    const levelGestureEvents = ["wheel", "pointermove", "pointerup", "dblclick", "touchmove"] as const;
    const levelGestureHost = host;
    for (const event of levelGestureEvents) {
      levelGestureHost.addEventListener(event, scheduleLevelPositionRefresh, { passive: true });
    }
    const focusRangeEvents = ["wheel", "pointerup", "touchend"] as const;
    for (const event of focusRangeEvents) {
      levelGestureHost.addEventListener(event, scheduleFocusRangePersist, { passive: true });
    }
    chart.timeScale().subscribeVisibleLogicalRangeChange(publishVisibleRanges);
    publishVisibleRanges();
    paint(tokens);
    const stop = watchTheme(host, (t) => { tokens = t; paint(t); });
    return () => {
      unsubscribeServedVenueState();
      window.removeEventListener(CVD_MODE_EVENT, syncUncontrolledMode);
      stop();
      resizeObserver.disconnect();
      for (const event of levelGestureEvents) {
        levelGestureHost.removeEventListener(event, scheduleLevelPositionRefresh);
      }
      for (const event of focusRangeEvents) {
        levelGestureHost.removeEventListener(event, scheduleFocusRangePersist);
      }
      if (levelPositionFrame !== null) cancelAnimationFrame(levelPositionFrame);
      if (focusPersistFrame !== null) cancelAnimationFrame(focusPersistFrame);
      chart?.timeScale().unsubscribeVisibleLogicalRangeChange(publishVisibleRanges);
      chart?.remove();
      chart = null;
      cvdGapMarkerLayer = null;
      divergencePriceMarkers = null;
      divergenceCvdMarkers = [];
    };
  });

  /**
   * ⛔⛔⛔ R847① — THE CHART RELABELLED, REFETCHED, AND NEVER REDREW.
   *
   * Both `setData` calls lived INSIDE `onMount`. They ran **once**, at mount, and nothing fed the
   * series again for the life of the page. Press a timeframe and: the header relabels (it is
   * `$derived`), `candles?tf=180` returns 219 bars, and **the series is never told** — so the
   * candles, the x-axis and the CVD line stay the 1-minute picture the pane happened to mount with.
   * The desk's before/after clips at 3h were PIXEL-IDENTICAL while the header read "rung 3h · 219
   * price bars". ***That is the owner's sentence, and it was a lifecycle bug, not a wiring one.***
   *
   * 🔑 **AND IT IS WHY EVERY ARM PASSED.** The binding is declared (the AST arm sees it), the pane
   * mounts (the render arm sees it), the request leaves the browser (the wire arm sees it), the
   * header changes (my own arm saw it). ⇒ ***RELABEL, REFETCH AND REPAINT ARE THREE CLAIMS, AND
   * ONLY THE THIRD IS THE PICTURE THE OWNER IS LOOKING AT.*** Nothing in this repo asserted the
   * third until now, which is exactly ax's `M-FROZEN` one layer down.
   *
   * `onMount` keeps the FIRST feed deliberately: `priceSeries` is a plain `let`, so an effect that
   * merely guarded on it would not re-run when it became non-null — it would return early once and
   * never draw anything. The effect below owns every SUBSEQUENT feed.
   */
  /**
   * ORDER 65 — **ONE DEFINITION, BECAUSE THERE ARE TWO CONSTRUCTION SITES.** The cvd candle series
   * is built at mount AND rebuilt inside `feedSeries` via `ensureUniqueNativeSeries`. I first put
   * the autoscale provider on the mount site only, and the fix was INERT: the series that actually
   * renders is the one the factory rebuilds. ⇒ ***AN OPTION SET ON THE COPY THAT IS NOT REBUILT IS
   * NOT SET AT ALL*** — the same shape as a guard that is correct on main and degraded where it
   * executes. Both sites now call this; a future option cannot be added to one and missed by the other.
   */
  function cvdCandleOptions() {
    return {
      title: "", // A7: native scale ink never exposes the implementation ID.
      priceScaleId: "right" as const,
      borderVisible: false,
      priceLineVisible: false,
      lastValueVisible: true,
      /**
       * Legacy wickless bars stay DRAWN but may not own this scale — at 3h they span ~285k against
       * the measured bars' ~48k, and LWC fits a scale from every series on it.
       * ⛔ THE SENTENCE THAT STOOD HERE — "`null` hands the pane back to the engine when no measured
       * bar exists" — WAS MEASURED FALSE: `null` yields LWC's DEGENERATE {-0.5, 0.5}, a blank pane.
       * It is struck rather than deleted because it is exactly the sentence a future reader would
       * cite to revert the fallback to `null`. See cvdCandleAutoscaleRange, which no longer can.
       */
      autoscaleInfoProvider: () => {
        const visibleRange = chart?.timeScale().getVisibleRange();
        const visibleCvdCandles = visibleRange === null || visibleRange === undefined
          ? cvdCandleModel.candles
          : cvdCandleModel.candles.filter((point) =>
              point.t >= Number(visibleRange.from) * 1_000 && point.t <= Number(visibleRange.to) * 1_000);
        const priceRange = cvdCandleAutoscaleRange(
          visibleCvdCandles.length > 0 ? visibleCvdCandles : cvdCandleModel.candles,
          CVD_CANDLE_AUTOSCALE_MARGIN_FRACTION,
          CVD_CANDLE_AUTOSCALE_MIN_MARGIN,
        );
        return priceRange === null ? null : { priceRange };
      },
    };
  }

  function feedSeries() {
    if (!chart || !priceSeries || !cvdCandleSeries || !tokens) return;
    /**
     * R1 (ORDER_HUD_CALIBRATION_1 round 2, split confirmed by R2534/R2545): read the eye's position
     * BEFORE the feed, and anchor it by TIME. A prepend, a trim or an inserted timestamp shifts
     * logical indices under an unmoved eye, so a logical range restores the wrong window; the
     * visible TIME interval is the thing the owner actually chose.
     *
     * ⛔ FOLLOW IS DERIVED, NEVER A SECOND STICKY FLAG. chartViewport's header is explicit that a
     * stored boolean can disagree with what the screen shows, so we ask the same question it asks:
     * did the visible right edge still cover the newest bar we had? `fedLast` is that bar, in the
     * pane's own seconds, so the comparison is unit-consistent with `rangeBefore.to`.
     */
    const rangeBefore = chart?.timeScale().getVisibleRange() ?? null;
    const followingBefore =
      rangeBefore === null || fedLast === null || coversLiveEdge(Number(rangeBefore.to), fedLast);
    const retainedCvdCandleSeries = cvdCandleSeries;
    const attachedOwners = chart.panes().flatMap((pane) => pane.getSeries())
      .filter((series): series is ISeriesApi<"Candlestick"> => nativeCvdOwners.has(series as ISeriesApi<"Candlestick">));
    const retainedAttached = attachedOwners.includes(retainedCvdCandleSeries);
    cvdCandleSeries = retainedAttached
      ? retainedCvdCandleSeries
      : attachedOwners[0] ?? chart.addSeries(CandlestickSeries, cvdCandleOptions(), CVD_LINE_PANE);
    nativeCvdOwners.add(cvdCandleSeries);
    for (const duplicate of attachedOwners) {
      if (duplicate !== cvdCandleSeries) chart.removeSeries(duplicate);
    }
    const ownerPane = chart.panes().find((pane) => pane.getSeries().includes(cvdCandleSeries!));
    if (ownerPane && ownerPane.paneIndex() !== CVD_LINE_PANE) cvdCandleSeries.moveToPane(CVD_LINE_PANE);
    if (cvdCandleSeries !== retainedCvdCandleSeries) {
      zeroLine = null;
      cvdPivotMarkers = [createSeriesMarkers(cvdCandleSeries, [])];
      cvdGapMarkerLayer = createSeriesMarkers(cvdCandleSeries, []);
      divergenceCvdMarkers = [createSeriesMarkers(cvdCandleSeries, [])];
    }
    const cs = candles;
    priceSeries.setData(cs.map((c) => ({
      time: secs(c.t), open: c.o, high: c.h, low: c.l, close: c.c,
    })));
    cvdCandleSeries.applyOptions({
      title: "",
      upColor: tokens["--cvd-pos"], downColor: tokens["--cvd-neg"],
      wickUpColor: tokens["--cvd-pos"], wickDownColor: tokens["--cvd-neg"],
      borderVisible: false, priceLineVisible: false, lastValueVisible: true,
    });
    const cvdData = [
      ...cvdCandleModel.candles.map((point) => ({
        time: secs(point.t),
        open: point.open,
        high: point.high,
        low: point.low,
        close: point.close,
      })),
      ...cvdCandleModel.whitespace.map((point) => ({ time: secs(point.t) })),
    ].sort((a, b) => Number(a.time) - Number(b.time));
    cvdCandleSeries.setData(cvdData as never);
    if (fastSeries && slowSeries) {
      const mc = cs.length === 0 ? [] : mcStudy.slice(-cs.length);
      fastSeries.setData(mc.map((bar, i) => bar.mcFast === null
        ? { time: secs(cs[i].t) }
        : { time: secs(cs[i].t), value: bar.mcFast }));
      slowSeries.setData(mc.map((bar, i) => bar.mcSlow === null || bar.mcFast === null
        ? { time: secs(cs[i].t) }
        : {
            time: secs(cs[i].t), value: bar.mcSlow,
            color: bar.mcFast > bar.mcSlow ? tokens!["--up"] : tokens!["--dn"],
          }));
    }
    syncDivergenceSeries(tokens);
    cvdPivotMarkers.forEach((markers, index) => markers.setMarkers([...new Map(divergences.filter((pair) => {
      const segment = cvdCandleSeries?.data() ?? [];
      return segment.some((point) => Number(point.time) === secs(pair.swingAt));
    }).flatMap((pair) => [
      [pair.previousSwingAt, pair.previousCvd],
      [pair.swingAt, pair.currentCvd],
    ])).entries()].map(([t]) => ({
      time: secs(t), position: "inBar" as const, shape: "circle" as const,
      color: tokens!["--flip"], text: "", size: 1,
    }))));
    cvdGapMarkerLayer?.setMarkers(cvdGapOverlay.map((gap) => ({
      time: secs(gap.t), position: "inBar" as const, shape: "square" as const,
      color: tokens!["--stale"], text: "", size: 1,
    })));
    pushLevels();
    const first = cs.length ? secs(cs[0].t) : null;
    const last = cs.length ? secs(cs[cs.length - 1].t) : null;
    /**
     * ⛔⛔ GK-ax's COND, AND HE REFUTED MY PREMISE WITH THE TYPINGS.
     * This used to publish the INPUT array — *"written in the same breath as `setData`"*. He drove
     * `M-SELFREPORT`: neuter BOTH `setData` calls, leave the attribute updating, and **the arm
     * PASSES while the canvas is never fed.**
     * ⇒ ***"WRITTEN IN THE SAME BREATH" IS NOT "THE SAME ACT" — IF `setData` DOES NOT EXECUTE, THE
     * ATTRIBUTE STILL DOES.*** A self-report beside the act is not a report OF the act.
     *
     * And my justification was factually wrong, which is what makes the cure cheap. I wrote that
     * `lightweight-charts` *"exposes no readable series data"*. **It does:** v5.2.1 declares
     * `data(): readonly TData[]` (`typings.d.ts:2498`, verified at my hand on the installed copy).
     * ⇒ ***CHECK THE DEPENDENCY BEFORE BUILDING A WORKAROUND FOR ITS LIMITATION — I INVENTED AN
     * OBSERVABLE TO REPLACE ONE THAT ALREADY EXISTED.***
     *
     * So the span is READ BACK OUT OF THE SERIES. If `setData` did not run, `data()` returns the
     * previous bars and the attribute reports the stale span — which is the truth.
     * ⚠ STILL NOT THE PAINT (R850①): this is what the series HOLDS, not what the GPU drew. The
     * noun is `fed`, and it stays `fed`.
     */
    const drawn = priceSeries.data() as readonly { time: unknown }[];
    const t0 = drawn.length ? Number(drawn[0].time) : null;
    const t1 = drawn.length ? Number(drawn[drawn.length - 1].time) : null;
    if (host) {
      const ownerMemberships = chart.panes().filter((pane) => pane.getSeries().includes(cvdCandleSeries!));
      const attachedOwnerCount = chart.panes().flatMap((pane) => pane.getSeries())
        .filter((series) => nativeCvdOwners.has(series as ISeriesApi<"Candlestick">)).length;
      host.dataset.fed = `${drawn.length}:${t0 ?? ""}:${t1 ?? ""}`;
      host.dataset.studySeries = JSON.stringify({
        cvdCandle: {
          id: `cvdCandle:${cvd?.root ?? "UNDECLARED"}:${timeframe}`,
          data: cvdCandleSeries.data(),
          attachment: {
            attached: ownerMemberships.length === 1 && attachedOwnerCount === 1 && ownerMemberships[0].paneIndex() === CVD_LINE_PANE,
            attachedCount: ownerMemberships.length,
            ownerCount: attachedOwnerCount,
            paneIndex: ownerMemberships.length === 1 ? ownerMemberships[0].paneIndex() : null,
          },
          wicklessCount: cvdCandleModel.wicklessCount,
          wickless: cvdCandleModel.candles.filter((point) => point.wickless).map((point) => secs(point.t)),
        },
        cvdReadback: cvdModel.readback,
        retiredSeriesIds: ["cvdCumulative:*"],
        cvdBuckets: cvdIntensity.map((bar) => ({ time: secs(bar.t), bucket: bar.bucket })),
        cvdMarkers,
        divergences: divergenceSeries.map((entry) => ({
          id: entry.id.startsWith("cvdDivCvd:")
            ? entry.id.replace("cvdDivCvd:", "cvdSwingLine:")
            : entry.id,
          series_id: entry.id,
          swing_at: entry.pair.swingAt,
          confirmed_at: entry.pair.confirmedAt,
          w: entry.pair.w,
          data: entry.series.data(),
        })),
        cvdLinePane: {
          candle: { id: `cvdCandle:${cvd?.root ?? "UNDECLARED"}:${timeframe}`, data: cvdCandleSeries.data() },
          pivots: cvdPivotMarkers.flatMap((markers) => markers.markers()),
          ligatures: divergenceSeries.filter((entry) => entry.id.startsWith("cvdDivCvd:")).map((entry) => ({
            id: entry.id, legacy_id: entry.id.replace("cvdDivCvd:", "cvdSwingLine:"), data: entry.series.data(), confirmed_at: entry.pair.confirmedAt,
          })),
        },
        ghost: ghostSeries.map((entry) => ({
          id: entry.id, class: entry.segment.className, thresholds: entry.segment.thresholds,
          appeared_at: entry.segment.appearedAt, resolved_at: entry.segment.resolvedAt,
          near_wall: entry.segment.nearWall, data: entry.series.data(),
        })),
        vSplitFast: fastSeries?.data().length ?? 0,
        vSplitSlow: slowSeries?.data().length ?? 0,
      });
      host.dataset.lwcSeriesTitles = JSON.stringify(
        chart.panes().flatMap((pane) => pane.getSeries()).map((series) => String(series.options().title ?? "")),
      );
    }
    updateSessionZero(tokens);
    pinPaneHeights();
    publishVisibleRanges();
    /**
     * Re-frame ONLY when the span actually moved. A rung change rewrites the whole time domain and
     * must re-frame; a routine tail append must not, or the chart would yank itself back every poll
     * and fight the owner's own pan and zoom.
     *
     * ⛔ FOLLOWING LANDS ON THE RULED WINDOW, NEVER ON fitContent (5a26f4b5 box 1 + CHART-MOUNT-
     * READABLE-1 1036f30d, R2901/R2902). fitContent framed the WHOLE fed span - PrimaryChartPanel's
     * 300-bar fetch - so the cockpit landed on 301 bars with no gutter, about 5 h at 1m on a 796 px
     * host (HUDCHAT-38 measured 14:19Z 09-24 on the served apex): the owner's "unreadable". The
     * ruled window (R2, landingRangeFor) ran only behind focusHeight, i.e. maximized. It now runs on
     * the cockpit too, from the pane's MEASURED plot at its 0.08 gutter, anchored at the live edge.
     */
    if (first !== fedFirst || last !== fedLast) {
      if (followingBefore) {
        if (cs.length > 0) chart?.timeScale().setVisibleLogicalRange(landingRangeFor(landingCount(cs)));
      } else if (rangeBefore) {
        // INSPECTING: the span moved under the eye, so put the eye back where it was.
        chart?.timeScale().setVisibleRange(rangeBefore);
      }
    }
    if (focusHeight !== null && focusRungKey !== null && cs.length > 0 &&
        (focusAppliedRung !== focusRungKey || first !== fedFirst || last !== fedLast)) {
      const wanted = readFocusRange(focusRungKey) ?? landingRangeFor(landingCount(cs));
      chart.timeScale().setVisibleLogicalRange(wanted);
      focusAppliedRung = focusRungKey;
    }
    fedFirst = first; fedLast = last;
  }

  /** Deliberately NOT `$state`: written from inside the effect, and reading them must not re-trigger it. */
  let fedFirst: number | null = null;
  let fedLast: number | null = null;

  $effect(() => {
    // Track both series' inputs; `feedSeries` re-reads them and is a no-op before the series exist.
    void candles; void cvdModel; void cvdCandleModel; void cvdGapOverlay;
    void cvdIntensity; void cvdMarkers; void divergences; void mcStudy;
    feedSeries();
  });
</script>

<script module lang="ts">
  type SessionChartThemeTokens = Record<
    "--surface" | "--muted" | "--line" | "--faint" | "--accent",
    string
  >;

  export type SessionChartMount = "landing" | "focus";

  /** One resolved-token bridge feeds both the native first paint and every later repaint. */
  export function sessionChartOptions(t: SessionChartThemeTokens, _mount: SessionChartMount) {
    return {
      layout: {
        background: { color: t["--surface"] },
        textColor: t["--muted"],
        attributionLogo: false,
        panes: {
          separatorColor: t["--faint"],
          separatorHoverColor: t["--accent"],
          enableResize: true,
        },
      },
      grid: {
        vertLines: { color: t["--line"] },
        horzLines: { color: t["--line"] },
      },
      crosshair: {
        mode: 0 as const,
        vertLine: { color: t["--faint"] },
        horzLine: { color: t["--faint"] },
      },
      rightPriceScale: { borderColor: t["--line"] },
      leftPriceScale: { borderColor: t["--line"], visible: false },
      timeScale: { borderColor: t["--line"], timeVisible: true, secondsVisible: false },
    };
  }
</script>

  <section
  class="scvd"
  class:focus-fit={focusHeight !== null}
  aria-label="ES price with continuous or session CVD"
  data-study-stack={stackMarker}
  data-chart-info-open={String(showInfo)}
  data-cvd-study={cvdStudy}
  data-cvd-provenance={cvdProvenance}
  data-cvd-delta-summary={`CVD Δ · SESSION RELATIVE · ABS? ${absorptionCount} · EXH? ${exhaustionCount} · HYPOTHESIS`}
  data-vsplit-descriptor="V-SPLIT · MC Stoch 40/81 · fast above · fast below"
  data-session-anchor-state={anchored ? "ANCHORED" : "NO SESSION ANCHOR"}
  data-cvd-transform={cvdTransform ?? ""}
    data-cvd-anchor={servedAnchor.state}
    data-cvd-session-key={servedAnchor.sessionKey ?? ""}
    data-cvd-roll-readback={JSON.stringify(cvdModel.readback)}>
  <header>
    <!--
      THREE STATES, and the empty string is a DELIBERATE third one rather than a falsy accident:
        null ...... standalone page — name yourself, exactly as before this prop existed
        "…" ....... the host names you (root + THAT chart's rung)
        "" ........ the host titles the panel itself ⇒ ** emit nothing **, one marker per chart
      ⚠ `label ?? default` alone would render the default for "" and re-create the duplicate.
    -->
    {#if label !== ""}<b>{label ?? "ES · PRICE · CVD GLOW · V-SPLIT"}</b>{/if}
    {#if standaloneNote}<span class="sub">one time axis · heads-up only</span>{/if}
    {#if label !== ""}
      <button
        class="chart-info-toggle"
        data-chart-info-toggle
        aria-label="chart information"
        aria-controls={infoPanelId}
        aria-expanded={showInfo}
        onclick={() => (showInfo = !showInfo)}>ⓘ</button>
    {/if}
    <button
      class="cvd-mode-toggle"
      type="button"
      data-cvd-mode-toggle
      aria-label={`CVD display mode: ${cvdMode}`}
      aria-pressed={cvdMode === "continuous"}
      onclick={toggleCvdMode}>
      <span class:active={cvdMode === "continuous"}>CONT</span>
      <span aria-hidden="true">|</span>
      <span class:active={cvdMode === "session"}>SESSION</span>
    </button>
    <span class="age face-hyp" data-hyp>HYP</span>
    {#if reserveVerdictSlot}<span data-verdict-slot="reserved"></span>{/if}
    <div id={infoPanelId} class="chart-info-panel" data-chart-info-panel hidden={!showInfo}>
      <span class="chip hyp">{cvdStudy}</span>
      {#if declaredResidue.length > 0}
        {#if residueAbsent.length > 0}<span class="studylabel" data-spine-residue>RESIDUE · {residueAbsent.length} declared spine minute(s)</span>{/if}
        {#each declaredResidue as residue (residue.minute)}
          <span class="studylabel">{residue.minute} · {residue.ruling}
            {#if residue.state === "FILLED"} · FILLED · {residue.filled_by}{/if}
          </span>
        {/each}
      {/if}
      {#if cvdModel.readback.roll_buckets > 0}
        <span class="studylabel" data-cvd-roll-readback>CVD roll/gap readback · {JSON.stringify(cvdModel.readback)}</span>
      {/if}
      <!-- ORDER 77: the anchor state is INK. Amber when the server did not anchor, because a
           plausible-looking wrong LEVEL is worse than a visible refusal (law 1, fail LOUD). -->
      <span class="chip" class:hyp={servedAnchor.state === "ANCHORED"}
            class:stale={servedAnchor.state === "UNANCHORED"}>
        {servedAnchor.state === "ANCHORED"
          ? `ANCHORED · ${servedAnchor.sessionKey}`
          : "UNANCHORED · drawn as served"}
      </span>
      <span class="studylabel"><b>CVD</b> · CONT draws served spine unchanged; SESSION rebases once at 17:00 CT · {cvdProvenance}</span>
      <span class="chip hyp">CVD Δ · SESSION RELATIVE · ABS? {absorptionCount} · EXH? {exhaustionCount} · HYPOTHESIS</span>
      <!--
        ⛔ THE GHOST'S STATUS IS THE DESK'S, AND IT IS NO LONGER A RATE TYPED INTO THIS TEMPLATE.
        These chips used to read "64.7% follows · n=17 · HYPOTHESIS" (amber) and "SNAP · 72.7% ·
        +7.1t @15m · n=22" (violet). R1297 read PREREG_004 and killed amber outright — n = 1,291 and
        1,002 against a baseline it beats in neither form nor half — and left violet a HYPOTHESIS
        resting on one TRAIN cell that did not clear α. Continuing to ship "64.7% follows" beside a
        dead study is the hub asserting a rate the desk has retired, which is what law 6 forbids.
        The PREFIX below is the marker's MEANING (what the ghost is), which the reading did not
        change; the VERDICT and its qualifier come from the reading module and nowhere else.
      -->
      {#each renderedGhostSegments as ghost (ghost.id)}
        {@const badge = ghostBadge(ghost.className)}
        <span
          class="chip"
          class:hyp={badge.emphasis === "ink"}
          data-ghost-verdict={badge.verdict}
          data-ghost-marker={ghost.className}>
          {ghost.className === "amber"
            ? `ABS? at ${ghost.nearWall ? "wall" : "flow lead"}`
            : ghost.resolvedAt !== null
              ? "SNAP"
              : "vacuum — do NOT fade"}
          · {badge.verdict} · {badge.qualifier} · {PREREG_004_READING.ruling}
        </span>
      {/each}
      {#if !intensityHasPreviousFloor}<span class="chip warn">INTENSITY · SESSION-RELATIVE ONLY</span>{/if}
      <span class="pane2legend vsplit">
        <b>V-SPLIT</b> · {vsplitLegend} · {vsplitRungLabel}
        {#if venueClosed} · session {vsplitSessionDate}{/if} ·
        <span class="sw above"></span> fast above · <span class="sw below"></span> fast below
      </span>
      {#if vsplitWarmupReason !== null}
        <span class="chip warn" data-vsplit-warmup-reason title={vsplitWarmupReason}>{vsplitWarmupReason}</span>
      {/if}
      <!--
        ⛔ THE LIGATURES CARRY BOTH HALVES OF THEIR STATE. R1297 records the CVD divergence
        ligatures (close-form and wick-form) as **HYPOTHESIS · DISPLAY ONLY — unchanged**, and
        "unchanged" is a statement about the VERDICT, not a claim about what this pane draws: the
        chip said HYPOTHESIS and stopped there. HYPOTHESIS alone says "not yet proven"; DISPLAY ONLY
        says "do not trade this", and the sibling panels that hold markers of the same class
        (TidePanel, ExpiryTidePanel, RangeChartPanel) all print both. Half a declared state is the
        half a reader completes for themselves, in the direction they were already leaning.
      -->
      {#each divergences as pair (pair.id)}
        <span class="chip hyp" data-confirmed-at={pair.confirmedAt} data-study-state="HYPOTHESIS · DISPLAY ONLY">
          ABS? · {formatContracts(pair.contracts)} · price {pair.priceTicks >= 0 ? "+" : ""}{pair.priceTicks}t
          · confirmed {new Date(pair.confirmedAt).toISOString().slice(11, 16)}Z · HYPOTHESIS · DISPLAY ONLY
        </span>
      {/each}
      {#if chartRole === "higher" && !divergenceSupported}
        <span class="chip warn">CVD DIVERGENCE UNAVAILABLE · {timeframe} UNSTUDIED · HYPOTHESIS</span>
      {/if}
      {#if focusHeight !== null}
        <button type="button" class="focus-reset" data-focus-reset-zoom onclick={resetFocusZoom}>RESET ZOOM</button>
      {/if}
      {#if !anchored}<span class="chip warn">NO SESSION ANCHOR — continuous as served, no zero line drawn</span>{/if}
      {#if cvdTransform}<span class="transform">{cvdTransform}</span>{/if}
      {#if chartRole === "lower"}
        <div class="contracts-strip" data-contracts-strip>
          <b>CONTRACTS</b>
          <span>HOT {cvdIntensity.filter((bar) => bar.bucket === 3).length}</span>
          <span>ABS? {absorptionCount}</span>
          <span>EXH? {exhaustionCount}</span>
          <span class="chip hyp">HYPOTHESIS</span>
          {#if !markerEligible}<span class="chip warn">MARKERS · 1m+ ONLY</span>{/if}
        </div>
      {/if}
    </div>
    {#if markerGapPresentation}
      {#if markerGapPresentation.alert}
        <span class="chip warn" data-chart-alert>{markerGapPresentation.label}</span>
      {:else}
        <span class="chip">{markerGapPresentation.label}</span>
      {/if}
    {/if}
    <StalenessChip
      label="CVD"
      pollKey={cvdFeed}
      budgetS={cvdFeed?.budget_s ?? 180}
      ageS={cvdNoData ? null : cvdFeed?.age_s ?? null}
      error={cvdError}
      {lastBarMs}
      barSpanMs={cvdBarSpanMs}
      compactLive />
    <!-- ⛔ THE DEAD LEG IS NAMED IN THE HEADER, beside the other chips — a reader scanning the
         chip row must not have to notice an ABSENCE of candles to learn the feed is down. -->
    {#each gaps as gap (gap.leg)}
      <span class="chip warn" data-chart-alert>{gap.title}</span>
    {/each}
    <!--
      ⛔ AN OFF-FRAME LEVEL IS STATED, NOT SWALLOWED. The design board rejected "draw only what fits"
      in writing, for shipping a visibly unchanged chart while omitting facts about the book.
      A count in the chip row is the honest minimum; the edge tags it also ratified are OWED, and
      saying so here is better than a silence that reads like completeness.
    -->
    {#if showLevels && offFrame > 0}
      <span class="chip warn" data-chart-alert>{offFrame} level{offFrame === 1 ? "" : "s"} outside the frame</span>
    {/if}
    {#if showLevels && levelPositionErrors > 0}
      <span class="chip warn" data-chart-alert>LEVEL POSITION ERROR — {levelPositionErrors} badge{levelPositionErrors === 1 ? "" : "s"} withheld</span>
    {/if}
    <!-- ⛔ THE CLOCK'S PROVENANCE IS NAMED, NEVER INFERRED. BUILDER-bi handed this seat the choice
         in writing (18:32Z) — wait for the `t` deploy, or derive client-side — and closed it with
         *"either is fine; SILENCE IS NOT."* A fallback that works and says nothing is the worse
         failure: it removes all pressure to land the deploy and makes the pane's timing
         unknowable from the pane. These chips are that promise, kept. -->
    {#if bars.length && timing.source === "none"}
      <span class="chip warn" data-chart-alert>NO READABLE BAR TIME — {timing.total} bars served, none timed</span>
    {:else if timing.source === "bar_open" && bars.length}
      <span class="chip warn" data-chart-alert>CLOCK DERIVED from bar_open{timing.preCure ? " — route predates the `t` cure" : " — some bars lack `t`"}</span>
    {/if}
    {#if timing.unreadable > 0}
      <span class="chip warn" data-chart-alert>{timing.unreadable} of {timing.total} bars carry no readable time</span>
    {/if}
  </header>
  <!--
    ⛔⛔ UNMEASURED IS NOT SAME, AND UNTIL NOW THE FACE DREW THEM IDENTICALLY. This block rendered
    only for SEAM, so a door saying "I COULD NOT COMPARE the two contracts" produced exactly the
    same pixels as one saying "I COMPARED THEM AND THEY AGREE" — nothing. That is absence coerced
    to the most-preferred value, which is the defect cvd2.ts's own §4a law exists to prevent, and
    AGENTS.md law 1 forbids a degraded mode without a visible state.
    ⚠ THE INFORMATION WAS NEVER MISSING, ONLY THE FACE WAS: PrimaryChartPanel's `contractNote`
    already spells all three states out — but it lives behind the ⓘ toggle, and a reader who does
    not open the toggle is told nothing. A note behind a toggle is not a visible state.
    ⇒ ONE PILL, THREE STATES: SEAM keeps its warn pill; UNMEASURED gets a DASHED pill, because a
      dashed border is this estate's mark for a claim that was not made (Gex2Panel's `.chip.unserved`)
      rather than an alarm; SAME stays silent, which is the one state where silence is honest.
      It stays ONE element on purpose — cvd2.test.ts A2 counts the seam-pill marker attribute in the
      pane body and requires exactly one, so a second element would break that count while looking
      harmless. (Naming that attribute literally in this comment ALSO breaks it: the arm counts
      occurrences in the file, not elements in the DOM, and it caught this comment doing exactly
      that on the first run of this box. An arm that greps source counts YOUR PROSE too.)
    The door's state is a MOVING TARGET, not a fact about tonight: it read UNMEASURED across the
    CME break and SAME in RTH on the same code, so this is written against the STATE, never against
    the value the door happened to serve when the box was written.
  -->
  {#if cvdFeed?.contract_seam?.state === "SEAM" || cvdFeed?.contract_seam?.state === "UNMEASURED"}
    <span
      class="chip {cvdFeed.contract_seam.state === 'SEAM' ? 'warn' : 'unmeasured'}"
      data-contract-seam-pill
      data-seam-state={cvdFeed.contract_seam.state}
    >{cvdFeed.contract_seam.state === "SEAM"
        ? `CONTRACT SEAM · tape ${cvdFeed.contract_seam.tape_instrument_id ?? "unknown"}`
        : `CONTRACT UNMEASURED — ${cvdFeed.contract_seam.reason ?? "no reason served"}`}</span>
  {/if}
  <!--
    ⛔ THE TAGS ARE PINNED TO THE PRICE PANE, NOT THE CARD. Pane 0 is 62% of the chart height (set in
    `onMount`), so a tag flush to the bottom of the CARD would sit against the CVD pane and claim a
    price position on the wrong axis.
  -->
  <div class="hostwrap">
    <div class="host" bind:this={host}
      style:height={focusHeight === null ? `${height}px` : undefined}></div>
    {#if windowShade}
      <div class="windowshade" style={`left:${windowShade.left}px;width:${windowShade.width}px`} aria-hidden="true"></div>
    {/if}
    {#if showLevels && inFrameTags.length}
      {#each inFrameTags as tag, i (tag.text + i)}
        <!-- FIX ROUND 1 desk ruling: the dealer badge is ALERT-class ink and must stay visible;
             hiding it makes the geometry gate measure a zero rectangle instead of the real badge. -->
        <div class="lvlbadge {tag.cls} inframe" style={`top:${tag.top}px`} data-chart-alert>
          <span>{tag.text}</span>
        </div>
      {/each}
    {/if}
    {#if showLevels && edgeTags.length}
      {#each edgeTags as tag, i (tag.side + tag.text + i)}
        <div
          class="lvledge {tag.cls} {tag.side}"
          hidden={!showInfo}
          style={tag.side === "below" ? `top:${Math.max(0, pane0Height - 16)}px` : ""}
        >
          <span class="lvlarrow">{tag.side === "above" ? "▲" : "▼"}</span>
          <span class="lvltext">{tag.text}</span>
        </div>
      {/each}
    {/if}
  </div>
  <!-- ⛔ BOTH LEGS, INDEPENDENTLY. This was `{#if !bars.length}` alone, and a dead candles leg
       therefore painted pane 0 BLANK in silence on the owner's served page (measured 11:4xZ:
       price-pane ink 0 px, no chip, header still claiming "served"). The legs are decided in
       `paneGaps` so the decision is armable — the markup is not. -->
  {#if cvdNoData}
    <p class="msg" data-chart-alert><b>NO DATA</b><small>{cvdModel.blocked}</small></p>
  {:else if cvdPaneBlocked}
    <!-- The refusal's OWN SENTENCE, not a generic gap line. cvd2BlockedReason names the reason
         (wrong root · absent root · contract drift · route refused) and the reader gets it verbatim. -->
    <p class="msg" data-chart-alert><b>CVD REFUSED</b><small>{cvdPaneBlocked}</small></p>
  {/if}
  {#each gaps as gap (gap.leg)}
    <p class="msg" data-chart-alert><b>{gap.title}</b><small>{gap.detail}</small></p>
  {/each}
</section>

<style>
  .scvd {
    display: block;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 8px 10px 10px;
  }
  .scvd.focus-fit { display: flex; height: 100%; min-height: 0; flex-direction: column; overflow: hidden; }
  header {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  header b { font-size: 13px; color: var(--ink); }
  .sub { font-size: 11px; color: var(--faint); }
  .chart-info-panel { display: contents; }
  .chart-info-panel[hidden] { display: none; }
  .chart-info-toggle {
    width: 20px; height: 20px; padding: 0; border: 1px solid var(--line); border-radius: 999px;
    background: var(--surface); color: var(--muted); cursor: pointer; font: 12px/18px var(--font-num);
  }
  .cvd-mode-toggle {
    display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border: 1px solid var(--line);
    border-radius: 999px; background: var(--surface); color: var(--muted); cursor: pointer;
    font: 10px/16px var(--font-num); letter-spacing: .04em;
  }
  .cvd-mode-toggle .active { color: var(--ink); font-weight: 700; }
  .focus-reset {
    border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px;
    background: var(--surface-2); color: var(--muted); cursor: pointer; font: 10px var(--font-num);
  }
  /* ⛔ `--muted`, NOT `--faint`. tokens.css states this rule in its own comment — "ON --chip
   * 3.93:1 ⛔ BELOW AA — do not put --faint [here]" — and NOTHING ENFORCED IT, so this rule
   * shipped the estate's only sub-AA text pair, live on the owner's domain for two days as an
   * unanswered [DESK-Q]. Measured on the token values themselves:
   *    light  --faint on --chip  3.93:1 ⛔     --muted on --chip  4.99:1 ✅
   *    dark   --faint on --chip  4.56:1 ✅     --muted on --chip  5.69:1 ✅
   * ⭐ NO NEW COLOUR IS INTRODUCED: `--muted` is already validated (5.91:1 on --surface).
   * ⚠ AND THE DARK CURE COULD NOT BE MIRRORED: dark fixed its own 4.01:1 by DARKENING THE
   * GROUND, but light's ground is already near-white — lightening the chip as far as it goes
   * reaches only 4.50:1 and leaves it invisible at 1.04:1 against --surface.
   * ⇒ THE INK HAD TO MOVE, NOT THE GROUND. The two themes needed opposite cures. */
  .chip { max-width: 100%; box-sizing: border-box; overflow-wrap: anywhere; font-size: 10px; padding: 1px 6px; border-radius: 999px; background: var(--chip); color: var(--muted); }
  .chip.hyp { color: var(--ink); }
  .face-hyp { padding: 1px 6px; border: 1px solid var(--line); border-radius: 999px; background: var(--chip); color: var(--ink); font: 700 10px var(--font-num); }
  .chip.flip { color: var(--ink); }
  .range-toggle { cursor: pointer; font: inherit; }
  .range-toggle:disabled { cursor: not-allowed; opacity: .55; }
  .pane2legend { display: inline-flex; max-width: 100%; align-items: center; gap: 4px; flex-wrap: wrap; font-size: calc(10px * var(--ink-scale)); color: var(--muted); }
  .sw { display: inline-block; width: 10px; height: 3px; border-radius: 2px; }
  .pane2legend.vsplit b { color: var(--ink); }
  .sw.above { background: var(--up); }
  .sw.below { background: var(--dn); }
  /* ⛔ NOT `--stale` as the text colour: tokens.css records that pairing at 3.14:1.
     `--stale-ink` exists precisely for text ON `--stale-soft` (5.59:1 light / 6.75:1 dark). */
  .chip.warn { background: var(--stale-soft); border: 1px solid var(--stale); color: var(--stale-ink); }
  /* UNMEASURED is not an alarm and must not borrow one. A DASHED border is the estate's mark for a
     claim that was not made (Gex2Panel's `.chip.unserved`); the tokens are theme-owned, so both
     themes follow without a second rule. Transparent background keeps it quieter than `warn`. */
  .chip.unmeasured { background: transparent; border: 1px dashed var(--stale); color: var(--stale-ink); }
  .host { width: 100%; }
  .focus-fit .hostwrap { width: 100%; min-height: 0; flex: 1 1 auto; overflow: hidden; }
  .focus-fit .host { height: 100%; min-height: 0; }
  .windowshade {
    position: absolute;
    inset-block: 0;
    z-index: 2;
    pointer-events: none;
    background: color-mix(in srgb, var(--accent) 8%, transparent);
    border-inline: 1px solid color-mix(in srgb, var(--accent) 32%, transparent);
  }
  .contracts-strip {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 6px;
    padding-top: 5px;
    border-top: 1px solid var(--line);
    color: var(--muted);
    font: 10px var(--font-num);
  }
  .contracts-strip b { color: var(--ink); }
  /*
   * EDGE TAGS (design board, Option C). Tokens only — law 8 — so both themes follow, and the
   * colours are the SAME ones the on-canvas line uses: a tag must read as the line continuing.
   */
  .hostwrap { position: relative; width: 100%; min-width: 0; }
  .lvlbadge {
    position: absolute;
    box-sizing: border-box;
    /* Order 7 moved this overlay into a 394px chart column. Its measured price axis is ~70px;
       reserve 77px for that axis plus a 6-8px safety gap, without wasting the narrow plot width. */
    right: 77px;
    z-index: 4;
    /* The right reserve plus an 8px left inset keeps even a long dealer label inside pane 0. */
    max-width: calc(100% - 85px);
    height: 15px;
    padding: 0 2px;
    transform: translateY(-50%);
    background: var(--surface);
    font: 10px/14px var(--font-num);
    letter-spacing: 0.04em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
    border-bottom: 1px solid currentColor;
  }
  .lvlbadge.magnet { color: var(--magnet); }
  .lvlbadge.gammaflip { color: var(--flip); }
  .lvledge {
    position: absolute;
    left: 0;
    /*
     * ⚠ CLEAR OF THE PRICE AXIS, MEASURED FROM A RENDER RATHER THAN GUESSED. At 56px the stub ran
     * over the top axis label and "7712.00" read as "12.00" — the tag was legible and it made the
     * CHART less so, which is not a trade this feature is allowed to make.
     * ⛔ It is a fixed inset because LWC does not expose its scale width; if the axis ever carries
     * more digits this needs re-measuring, and that is a stated bound rather than a silent one.
     */
    right: 84px;
    display: flex; align-items: center; gap: 4px;
    font: 10px var(--font-num);
    letter-spacing: 0.04em;
    pointer-events: none;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    /*
     * ⛔ ABOVE THE CANVAS, AND I FOUND THIS BY LOOKING RATHER THAN BY A GATE. Without it the LWC
     * canvas paints over the stubs: the ABOVE tag vanished entirely and the BELOW tag was clipped
     * by the pane separator, half-legible. Every arm was green — they assert the tag is BUILT and
     * PINNED, and neither can see that something was drawn on top of it.
     * ⇒ ***A STRUCTURAL ARM CANNOT SEE STACKING. ONLY A PIXEL CAN.***
     * ⚠ And an illegible stub is worse than the count it replaced, which is the whole reason the
     * board rejected Option A.
     */
    z-index: 3;
    /* A readable stub needs its own ground: the chart paints right up to the edge. */
    background: var(--surface);
    padding-left: 2px; padding-right: 2px;
  }
  /* The 2px stub, flush to the frame edge — the line, clipped, not a label about a line. */
  .lvledge.above { top: 0; border-top: 2px solid currentColor; padding-top: 1px; }
  /* `top` is set inline from `pricePaneH` — a LOCAL layout number, not a design token.
     ⚠ `check:tokens` reads any `--foo` as a token and demanded a definition for a made-up one;
     it was right to. A layout offset is not part of the palette and must not wear its syntax. */
  /* Pinned 16px clear of the pane separator so the stub is READ, not bisected by it. */
  .lvledge.below { border-bottom: 2px solid currentColor; padding-bottom: 1px; }
  .lvledge.magnet { color: var(--magnet); }
  .lvledge.gammaflip { color: var(--flip); }
  .lvlarrow { font-size: 9px; }
  .lvltext { color: inherit; }
  /* ⛔ AMBER, NOT GREY. codex review called this: a gap notice rendered in `--faint` reads as a
     caption, and the law asks for a visible STALE/ERROR state. Same validated pairing as
     `.chip.warn` — `--stale-ink` ON `--stale-soft`, never `--stale` as the text colour. */
  .msg {
    grid-column: 1 / -1;
    margin: 8px 0 0; font-size: 12px; color: var(--stale-ink);
    background: var(--stale-soft); border: 1px solid var(--stale);
    border-radius: 6px; padding: 6px 8px;
  }
  .msg b { display: block; color: var(--stale-ink); }
  @media (max-width: 480px) {
    .scvd { display: block; }
    header { flex-direction: row; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
  }
</style>
